import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Search, Filter, Unlink, Loader2, Check, Info, Users } from 'lucide-react';
import toast from 'react-hot-toast';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';

import caregiverService from '../../services/caregiverService';
import CaregiverStatusBadge, { STATUS_VALUES } from './statusBadge';
import AddCaregiverModal from './AddCaregiverModal';
import DeactivateLinkConfirm from './DeactivateLinkConfirm';
import CaregiverDetail from './CaregiverDetail';
import EmptyState from '../ui/EmptyState';

const FILTER_OPTIONS = ['All', ...STATUS_VALUES];

// Small uppercase label above each value in a caregiver row.
const ROW_LABEL = 'text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400';

const formatShortDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch (_) {
    return '';
  }
};

/**
 * Caregivers tab on Participant Details (FECOA-1321).
 * Includes:
 * - Search by name/ID/email (FECOA-1370, client-side over the loaded list)
 * - Status filter dropdown (FECOA-1370)
 * - Status badges per row (FECOA-1375)
 * - "+ Add Caregiver" entry into the search-first modal (FECOA-1369 + FECOA-1362)
 * - Per-row unlink shortcut (FECOA-1374)
 */
const CaregiversTab = ({ participantId, participantLabel, siteId, studyId, studyCode, onCaregiversChanged }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [allowCaregivers, setAllowCaregivers] = useState(false);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [filterOpen, setFilterOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [deactivateTarget, setDeactivateTarget] = useState(null);

  // The detail view renders inline when ?selected_caregiver=<id> is set.
  // Keeping it on the same route prevents a full ParticipantDetails remount
  // and makes "Back to Caregivers List" feel instant.
  const selectedCaregiverId = searchParams.get('selected_caregiver') || null;

  // FECOA-1319 hand-off: Add Participant flow may navigate here with
  // `?addCaregiver=1` — auto-open the modal so the SC continues straight in.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('addCaregiver') === '1') {
      setAddOpen(true);
      // Strip the query param so refreshes don't re-trigger
      const cleanSearch = new URLSearchParams(location.search);
      cleanSearch.delete('addCaregiver');
      navigate(
        { pathname: location.pathname, search: cleanSearch.toString(), hash: location.hash },
        { replace: true }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCaregivers = async () => {
    try {
      setLoading(true);
      const data = await caregiverService.listForParticipant(participantId);
      setAllowCaregivers(!!data.allow_caregivers);
      setRows(data.caregivers || []);
    } catch (err) {
      console.error('Error loading caregivers:', err);
      toast.error('Failed to load caregivers.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (participantId) loadCaregivers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [participantId]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (statusFilter !== 'All' && row.status !== statusFilter) return false;
      if (!q) return true;
      const cg = row.caregiver || {};
      return (
        (cg.full_name && cg.full_name.toLowerCase().includes(q)) ||
        (cg.first_name && cg.first_name.toLowerCase().includes(q)) ||
        (cg.last_name && cg.last_name.toLowerCase().includes(q)) ||
        (cg.email && cg.email.toLowerCase().includes(q)) ||
        (cg.caregiver_id && cg.caregiver_id.toLowerCase().includes(q)) ||
        (row.caregiver_id && row.caregiver_id.toLowerCase().includes(q))
      );
    });
  }, [rows, search, statusFilter]);

  // Header count always equals the number of rows currently shown so it never
  // contradicts the visible list under any search/status filter (FECOA-1578).
  const assignedCount = filteredRows.length;

  // setSearchParams navigates to "?<params>", which drops the #caregivers hash.
  // ParticipantDetails picks its tab from the hash (FECOA-1626), so losing it
  // bounced the SC back to the Info tab instead of opening the caregiver.
  const setSelectedCaregiver = (id) => {
    const next = new URLSearchParams(searchParams);
    if (id) next.set('selected_caregiver', id);
    else next.delete('selected_caregiver');
    navigate({ pathname: location.pathname, search: next.toString(), hash: location.hash });
  };

  const goToDetail = (row) => {
    const id = row.caregiver?.id || row.caregiver_id;
    if (!id) return;
    setSelectedCaregiver(id);
  };

  const clearDetail = () => {
    setSelectedCaregiver(null);
    // Re-load list once the SC returns — counts/status may have changed via edit/reset/deactivate
    loadCaregivers();
  };

  if (!allowCaregivers && !loading) {
    return (
      <EmptyState
        icon={Info}
        description={<>Caregiver support is not enabled for this study. Enable it from the Study Details &gt; Protocol Settings.</>}
      />
    );
  }

  // Inline caregiver detail (no route change). Selected via ?selected_caregiver=<id>.
  if (selectedCaregiverId) {
    return (
      <CaregiverDetail
        caregiverID={selectedCaregiverId}
        participantID={participantId}
        studyCode={studyCode}
        onBack={clearDetail}
      />
    );
  }

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          Assigned Caregivers ({assignedCount})
        </h3>
        <button
          type="button"
          onClick={() => setAddOpen(true)}
          className="btn btn-primary"
        >
          <Plus className="h-4 w-4" />
          Add Caregiver
        </button>
      </div>

      <div className="flex items-center gap-3 mb-4">
        <div className="flex-1 relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 dark:text-gray-500" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, ID, or email..."
            className="input pl-10 pr-3"
          />
        </div>
        <div className="relative z-50">
          <button
            type="button"
            onClick={() => setFilterOpen((open) => !open)}
            className="btn btn-secondary h-10"
          >
            <Filter className="h-4 w-4 text-gray-400 dark:text-gray-500" />
            Filter: {statusFilter}
          </button>
          {filterOpen && (
            <div className="absolute right-0 z-[100] mt-1.5 w-52 animate-slide-down overflow-hidden rounded-xl border border-gray-200 bg-white py-1 shadow-float dark:border-gray-700 dark:bg-gray-800">
              {FILTER_OPTIONS.map((opt) => {
                const selected = statusFilter === opt;
                return (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => {
                      setStatusFilter(opt);
                      setFilterOpen(false);
                    }}
                    className={`flex items-center justify-between w-full text-left px-3 py-2 text-sm transition-colors ${
                      selected
                        ? 'text-primary-700 dark:text-primary-300 font-medium bg-primary-50 dark:bg-primary-500/10'
                        : 'text-gray-800 hover:bg-gray-50 dark:text-gray-100 dark:hover:bg-white/[0.06]'
                    }`}
                  >
                    <span>{opt}</span>
                    {selected && <Check className="w-4 h-4 flex-shrink-0" />}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-10">
          <Loader2 className="h-6 w-6 animate-spin text-primary-600 dark:text-primary-400" />
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={Users}
          description={<>Please click the &lsquo;+ Add Caregiver&rsquo; button to add Caregivers for this Participant.</>}
        />
      ) : filteredRows.length === 0 ? (
        <EmptyState icon={Search} description="No caregivers found matching your criteria." />
      ) : (
        <div className="space-y-3">
          {filteredRows.map((row) => {
            const cg = row.caregiver || {};
            return (
              <div
                key={row.link_id}
                onClick={() => goToDetail(row)}
                className="grid grid-cols-12 items-center gap-4 rounded-lg border border-gray-200 bg-white px-4 py-3 transition-colors hover:border-gray-300 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:hover:border-gray-600 dark:hover:bg-white/[0.03] cursor-pointer"
              >
                <div className="col-span-3 min-w-0">
                  <div className={ROW_LABEL}>Name</div>
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {cg.full_name || cg.first_name
                      ? `${cg.first_name || ''} ${cg.last_name || ''}`.trim()
                      : '—'}
                  </div>
                  <div className="mt-1">
                    <CaregiverStatusBadge status={row.status} />
                  </div>
                  {row.status === 'Deactivated' && row.deactivated_at && (
                    <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      on {formatShortDate(row.deactivated_at)}
                    </div>
                  )}
                </div>
                <div className="col-span-3 min-w-0">
                  <div className={ROW_LABEL}>Caregiver ID</div>
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{row.caregiver_id}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    Relationship: {row.relationship_type || '—'}
                  </div>
                </div>
                <div className="col-span-5 min-w-0">
                  <div className={ROW_LABEL}>Contact</div>
                  <div className="truncate text-sm text-gray-900 dark:text-gray-100">{cg.email || '—'}</div>
                  {cg.phone && <div className="text-xs text-gray-500 dark:text-gray-400">{cg.phone}</div>}
                </div>
                <div className="col-span-1 flex justify-end">
                  {row.is_active && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeactivateTarget(row);
                      }}
                      title="Deactivate Link"
                      className="btn btn-danger-soft btn-sm btn-icon"
                    >
                      <Unlink className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {addOpen && (
        <AddCaregiverModal
          participantId={participantId}
          participantLabel={participantLabel}
          siteId={siteId}
          studyCode={studyCode}
          onClose={() => setAddOpen(false)}
          onCreated={() => {
            setAddOpen(false);
            loadCaregivers();
            onCaregiversChanged?.();
          }}
        />
      )}

      {deactivateTarget && (
        <DeactivateLinkConfirm
          link={deactivateTarget}
          onClose={() => setDeactivateTarget(null)}
          onConfirmed={() => {
            setDeactivateTarget(null);
            loadCaregivers();
            onCaregiversChanged?.();
          }}
        />
      )}
    </div>
  );
};

export default CaregiversTab;
